import { isNull, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { worldCountriesList } from '../../../../constants/worldCountriesList';
import { getWorldGeometryJSON } from '../../../../home/store';
import { IconButton } from '../../../../shared/button/IconButton';
import { Dropdown, DropdownOption } from '../../../../shared/dropdown/Dropdown';
import { Sort } from '../../../../shared/icons';
import { PageLoadingDots } from '../../../../shared/loading-dots/PageLoadingDots';
import { InformationTooltip } from '../../../../shared/tooltip';
import styles from '../../Analytics.module.scss';
import { getFetchingAnalytics, getJurisdictionCoverageMapAnalytics, getJurisdictionCoverageSelectedCountry, getOpinionAnalyticsScope, getSelectedCounterpartyCoverage, getSelectedTransactionCoverage, JurisdictionCoverageCountry, OpinionAnalyticsChart, OpinionCoverageAnalytics, setJurisdictionCounterpartyCoverage, setJurisdictionCoverageSelectedCountry, setJurisdictionTransactionCoverage, setSelectedCoverageJurisdiction } from '../../store';
import { getAvailableCounterpartyFieldsForScope, getAvailableTransactionFieldsForScope } from '../../store/utils';
import { JurisdictionCoverageChart } from './JurisdictionCoverageChart';
import { JurisdictionCoverageDetails } from './JurisdictionCoverageDetails';
import { UK } from '../../../../constants/opinion';
import { getInternetGatewayOpen } from '../../../../auth/login/store';
import DisabledWorldMap from '../../../../../assets/DisabledWorldMap.png';

const { lightestGrey } = styles;

interface JurisdictionCoverageChartWrapperProps {
    dimensions?: {
        height: number;
        width: number;
    };
    tileInView: null | OpinionAnalyticsChart;
    setTileInView: (tile: OpinionAnalyticsChart | null) => void;
    analyticsSpinner: boolean;
}

export const JurisdictionCoverageChartWrapper: React.FC<JurisdictionCoverageChartWrapperProps> = ({ dimensions, setTileInView, tileInView, analyticsSpinner }) => {
    const dispatch = useAppDispatch();
    const data = useAppSelector(getJurisdictionCoverageMapAnalytics);
    const fetchingAnalytics = useAppSelector(getFetchingAnalytics);
    const selectedCountry = useAppSelector(getJurisdictionCoverageSelectedCountry);
    const worldGeometry = useAppSelector(getWorldGeometryJSON);
    const scope = useAppSelector(getOpinionAnalyticsScope);
    const counterpartyCoverage = useAppSelector(getSelectedCounterpartyCoverage);
    const transactionCoverage = useAppSelector(getSelectedTransactionCoverage);
    const internetGatewayOpen = useAppSelector(getInternetGatewayOpen);

    const showSpinner = useMemo(() => worldGeometry.length === 0 || fetchingAnalytics.includes(OpinionAnalyticsChart.JURISDICTION_COVERAGE), [fetchingAnalytics, worldGeometry.length]);
    const toggleTileView = useCallback(() => setTileInView(isNull(tileInView) ? OpinionAnalyticsChart.JURISDICTION_COVERAGE : null), [setTileInView, tileInView]);

    const getDataByCountry = useCallback((name: string): OpinionCoverageAnalytics[] => {
        if (name === UK) {
            return data.filter(({ jurisdiction }) => ['England & Wales', 'Scotland'].includes(jurisdiction));
        }
        return data.filter(({ jurisdiction }) => jurisdiction === name);
    }, [data]);

    const opinionMapCountry: JurisdictionCoverageCountry[] = useMemo(() => worldCountriesList.map(country => {
        const data = getDataByCountry(country.name);
        const disabled = data.every(({ isPermitted }) => !isPermitted);
        return { ...country, data, disabled };
    }), [getDataByCountry]);

    const selectedCountryGeometry = worldGeometry.find(({ id }) => id === selectedCountry);

    const { height, width } = useMemo(() => {
        if (dimensions) {
            if (tileInView === OpinionAnalyticsChart.JURISDICTION_COVERAGE) {
                const svgWidth = ((dimensions.width - 20) / 4) * 3;
                const svgHeight = dimensions.height - 60;
                return { width: svgWidth, height: svgHeight };
            }
            const svgWidth = dimensions.width - 20;
            const svgHeight = dimensions.height - 60;
            return { width: svgWidth, height: svgHeight };
        }
        return { width: 1, height: 1 };
    }, [dimensions, tileInView]);

    const counterpartyCoverageValue = useMemo(() => isNull(counterpartyCoverage) ? null : { label: counterpartyCoverage, value: counterpartyCoverage }, [counterpartyCoverage]);
    const transactionCoverageValue = useMemo(() => isNull(transactionCoverage) ? null : { label: transactionCoverage, value: transactionCoverage }, [transactionCoverage]);
    const counterpartyCoverageOptions = useMemo(() => getAvailableCounterpartyFieldsForScope(scope).map(label => ({ value: label, label })), [scope]);
    const transactionCoverageOptions = useMemo(() => getAvailableTransactionFieldsForScope(scope).map(label => ({ value: label, label })), [scope]);

    const setCounterpartyCoverage = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(setJurisdictionCounterpartyCoverage(value));
    }, [dispatch]);

    const setTransactionCoverage = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(setJurisdictionTransactionCoverage(value));
    }, [dispatch]);

    const selectJurisdiction = (id: string) => {
        const countryInfo = worldCountriesList.find(({ geoId }) => geoId === id);
        if (countryInfo) {
            dispatch(setSelectedCoverageJurisdiction(countryInfo.name));
        }
    };

    const counterpartyCoverageSelect = useMemo(() => (
        <div className={styles.coverageDropdown}>
            <Dropdown
                testId='jurisdiction-counterparty-coverage'
                onChange={setCounterpartyCoverage}
                value={counterpartyCoverageValue}
                options={counterpartyCoverageOptions}
                isClearable={!isNull(transactionCoverage)}
                placeholder='Select Counterparty...'
                controlBackgroundColor={lightestGrey}
            />
        </div>
    ), [counterpartyCoverageValue, counterpartyCoverageOptions, setCounterpartyCoverage, transactionCoverage]);

    const transactionCoverageSelect = useMemo(() => (
        <div className={styles.coverageDropdown}>
            <Dropdown
                testId='jurisdiction-transaction-coverage'
                onChange={setTransactionCoverage}
                value={transactionCoverageValue}
                options={transactionCoverageOptions}
                isClearable={!isNull(counterpartyCoverage)}
                placeholder='Select Transaction Type...'
                controlBackgroundColor={lightestGrey}
            />
        </div>
    ), [transactionCoverageValue, transactionCoverageOptions, setTransactionCoverage, counterpartyCoverage]);

    useEffect(() => () => {
        dispatch(setJurisdictionCoverageSelectedCountry(null));
        dispatch(setSelectedCoverageJurisdiction(null));
    }, [dispatch]);

    if (internetGatewayOpen) {
        return (
            <div className={styles.jurisdictionChartWrapper}>
                <div className={styles.jurisdictionChartHeader} style={{ height: '30px' }}>
                    <div className={styles.jurisdictionChartTitle}>Transaction Analytics</div>
                </div>
                <div className={styles.disabledMapWrapper} style={{ height: height, width: width }}>
                    <img src={DisabledWorldMap} width={width} height={height} />
                    <div className={styles.disabledMapLabel}>This feature is currently unavailable, please use the table for all of your Opinions.</div>
                </div>
            </div>
        );
    }

    if (showSpinner || analyticsSpinner) {
        return <PageLoadingDots />;
    }

    if (tileInView === OpinionAnalyticsChart.JURISDICTION_COVERAGE) {
        return (
            <div className={styles.jurisdictionChartWrapper} data-testid='opinion-jurisdiction-coverage-full-screen-chart-wrapper'>
                <div className={styles.jurisdictionChartHeader} style={{ height: '40px' }}>
                    <div className={styles.jurisdictionChartTitle} data-testid='opinion-jurisdiction-coverage-full-screen-chart-title'>Transaction Analytics</div>
                    <div className={styles.jurisdictionChartDescription}><InformationTooltip content='A visualisation of the conclusions of both transaction and counterparty coverage per jurisdiction across your opinion portfolio.' /></div>
                    <div className={styles.expandIcon}><IconButton icon={Sort} onClick={toggleTileView} fontSize={20} /></div>
                </div>
                <div className={styles.splitViewWrapper} style={{ height: `${height}px` }}>
                    <div style={{ width: `${width}px`, height: '100%' }}>
                        <JurisdictionCoverageChart
                            height={height}
                            width={width}
                            worldGeometry={worldGeometry}
                            graphData={opinionMapCountry}
                            selectedCountry={selectedCountryGeometry}
                            onClick={selectJurisdiction}
                            counterpartyCoverage={counterpartyCoverage}
                            transactionCoverage={transactionCoverage}
                        />
                    </div>
                    <JurisdictionCoverageDetails height={height} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.jurisdictionChartWrapper} data-testid='opinion-jurisdiction-coverage-chart-wrapper'>
            <div className={styles.jurisdictionChartHeader} style={{ height: '40px' }}>
                <div className={styles.smallTileDropdownWrapper}>
                    <div className={styles.smallTileDropdown}>{counterpartyCoverageSelect}</div>
                    <div className={styles.smallTileDropdown}>{transactionCoverageSelect}</div>
                </div>
                <div className={styles.jurisdictionChartDescription}><InformationTooltip content='A visualisation of the conclusions of both transaction and counterparty coverage per jurisdiction across your opinion portfolio.' /></div>
                <div className={styles.expandIcon}><IconButton icon={Sort} onClick={toggleTileView} fontSize={20} /></div>
            </div>
            <JurisdictionCoverageChart
                height={height}
                width={width}
                worldGeometry={worldGeometry}
                graphData={opinionMapCountry}
                selectedCountry={selectedCountryGeometry}
                onClick={noop}
                counterpartyCoverage={counterpartyCoverage}
                transactionCoverage={transactionCoverage}
            />
        </div>
    );
};
